/**
 * Maps a OneDrive item webUrl to the URL "Edit in Browser" should actually open.
 *
 * Office `Doc.aspx` links often open with `action=default`, which leaves Excel in
 * view mode inside Teams web. Forcing `action=edit` makes "Edit in Browser" land
 * directly in Office Online edit mode. A .pdf webUrl points straight at the file bytes, which the
 * browser hands to its own PDF reader - annotations made there live only in the
 * reader and never reach the item. Forcing web=1 keeps SharePoint's viewer in the
 * loop so annotations save back to the same document and a later check-in picks
 * them up.
 *
 * @param {string | null | undefined} webUrl
 * @returns {string} the URL to open, or "" when nothing usable was supplied
 */
export function resolveEditInBrowserUrl(webUrl) {
  if (!webUrl || typeof webUrl !== "string") {
    return "";
  }
  const trimmed = webUrl.trim();
  if (!trimmed) {
    return "";
  }

  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    return trimmed;
  }

  const lowerPath = parsed.pathname.toLowerCase();
  const lowerFileName = (parsed.searchParams.get("file") ?? "").toLowerCase();
  const officeEditExts = [
    ".doc",
    ".docx",
    ".docm",
    ".dotx",
    ".dotm",
    ".xls",
    ".xlsx",
    ".xlsm",
    ".xlsb",
    ".xlt",
    ".xltx",
    ".xltm",
    ".ppt",
    ".pptx",
    ".pptm",
    ".potx",
    ".potm",
  ];

  const isOfficeDoc =
    officeEditExts.some((ext) => lowerPath.endsWith(ext)) ||
    officeEditExts.some((ext) => lowerFileName.endsWith(ext));

  if (isOfficeDoc) {
    parsed.searchParams.set("action", "edit");
    return parsed.toString();
  }

  if (!lowerPath.endsWith(".pdf")) {
    return trimmed;
  }

  parsed.searchParams.set("web", "1");
  return parsed.toString();
}
